import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';
import { toast } from 'sonner';

export interface SupplierRow {
  id: string;
  organization_id: string | null;
  name: string;
  slug: string;
  website: string | null;
  contact_email: string | null;
  contact_phone: string | null;
  default_discount: string | null;
  notes: string | null;
  is_active: boolean;
  country_code: string;
  created_at: string;
  // computed
  is_global: boolean;
  families_count: number;
  materials_count: number;
  discounts_count: number;
}

export interface FamilyRow {
  id: string;
  supplier_id: string;
  code: string;
  name: string;
  description: string | null;
  category: string | null;
  brand: string | null;
  sort_order: number;
  is_active: boolean;
  // computed for current org
  materials_count: number;
  discount_id: string | null;
  discounts: number[];
  discount_valid_until: string | null;
}

// =====================================================================
// Suppliers list with computed counts
// =====================================================================
export const useSuppliersList = () => {
  const { currentOrganizationId } = useCurrentOrganization();

  return useQuery({
    queryKey: ['suppliers-list', currentOrganizationId],
    enabled: !!currentOrganizationId,
    queryFn: async (): Promise<SupplierRow[]> => {
      const { data: suppliers, error } = await supabase
        .from('suppliers')
        .select('*')
        .order('organization_id', { ascending: false, nullsFirst: false })
        .order('name');
      if (error) throw error;

      // Get counts in batches
      const ids = (suppliers ?? []).map(s => s.id);
      if (ids.length === 0) return [];

      // F22 (2026-05-25) — Bug fix: prima usavamo `.select('supplier_id').range(0, 19999)`
      // ma Supabase ha max-rows = 1000 di default. Con 2700+ materiali totali, alcuni
      // supplier (es. Fassa con 330 mat) venivano troncati e contati come 0 prodotti.
      // Fix: una COUNT(*) HEAD per supplier (carica solo il numero, niente dati).
      // 3 supplier × 1 query = 3 query, ma sono leggere (head:true = solo conteggio).
      const matCountResults = await Promise.all(
        ids.map(id =>
          supabase
            .from('materials')
            .select('id', { count: 'exact', head: true })
            .eq('supplier_id', id)
            .eq('is_active', true),
        ),
      );
      const mc = new Map<string, number>();
      matCountResults.forEach((res, idx) => {
        mc.set(ids[idx], res.count ?? 0);
      });

      const [{ data: famsCount }, { data: discCount }] = await Promise.all([
        supabase.from('supplier_product_families').select('supplier_id').in('supplier_id', ids),
        supabase.from('customer_discounts').select('supplier_id').in('supplier_id', ids).eq('organization_id', currentOrganizationId!),
      ]);

      const countBy = (arr: any[] | null, key: string) => {
        const m = new Map<string, number>();
        (arr ?? []).forEach(r => m.set(r[key], (m.get(r[key]) ?? 0) + 1));
        return m;
      };
      const fc = countBy(famsCount, 'supplier_id');
      const dc = countBy(discCount, 'supplier_id');

      return (suppliers ?? []).map((s: any) => ({
        ...s,
        is_global: s.organization_id === null,
        families_count: fc.get(s.id) ?? 0,
        materials_count: mc.get(s.id) ?? 0,
        discounts_count: dc.get(s.id) ?? 0,
      })) as SupplierRow[];
    },
  });
};

// =====================================================================
// Families of a supplier with materials count and current org discounts
// =====================================================================
export const useSupplierFamilies = (supplierId: string | null) => {
  const { currentOrganizationId } = useCurrentOrganization();

  return useQuery({
    queryKey: ['supplier-families', supplierId, currentOrganizationId],
    enabled: !!supplierId && !!currentOrganizationId,
    queryFn: async (): Promise<FamilyRow[]> => {
      // F22 — Paginazione client-side per superare il max-rows = 1000 di Supabase.
      // Knauf ha 1324 materiali → senza paginazione il count per famiglia sbaglia.
      async function fetchAllMats(): Promise<Array<{ id: string; family_id: string | null }>> {
        const all: Array<{ id: string; family_id: string | null }> = [];
        const CHUNK = 1000;
        let from = 0;
        // Cap di sicurezza a 20K materiali per supplier (eviterebbe loop runaway).
        for (let i = 0; i < 20; i++) {
          const { data, error } = await supabase
            .from('materials')
            .select('id, family_id')
            .eq('supplier_id', supplierId!)
            .eq('is_active', true)
            .range(from, from + CHUNK - 1);
          if (error) throw error;
          if (!data || data.length === 0) break;
          all.push(...(data as Array<{ id: string; family_id: string | null }>));
          if (data.length < CHUNK) break;
          from += CHUNK;
        }
        return all;
      }

      const [{ data: fams, error: fErr }, mats, { data: discs }] = await Promise.all([
        supabase
          .from('supplier_product_families')
          .select('*')
          .eq('supplier_id', supplierId!)
          .order('sort_order'),
        fetchAllMats(),
        supabase
          .from('customer_discounts')
          .select('*')
          .eq('supplier_id', supplierId!)
          .eq('organization_id', currentOrganizationId!),
      ]);
      if (fErr) throw fErr;

      const matsByFamily = new Map<string, number>();
      (mats ?? []).forEach(m => {
        if (m.family_id) matsByFamily.set(m.family_id, (matsByFamily.get(m.family_id) ?? 0) + 1);
      });
      const discByFamily = new Map<string, any>();
      (discs ?? []).forEach(d => { if (d.family_id) discByFamily.set(d.family_id, d); });

      return (fams ?? []).map((f: any) => ({
        ...f,
        materials_count: matsByFamily.get(f.id) ?? 0,
        discount_id: discByFamily.get(f.id)?.id ?? null,
        discounts: (discByFamily.get(f.id)?.discounts ?? []) as number[],
        discount_valid_until: discByFamily.get(f.id)?.valid_until ?? null,
      })) as FamilyRow[];
    },
  });
};

// =====================================================================
// Supplier mutations
// =====================================================================
interface CreateSupplierInput {
  name: string;
  slug?: string;
  website?: string;
  contact_email?: string;
  contact_phone?: string;
  default_discount?: string;
  notes?: string;
}

const slugify = (s: string) =>
  s.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

export const useCreateOrgSupplier = () => {
  const { currentOrganizationId } = useCurrentOrganization();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateSupplierInput) => {
      if (!currentOrganizationId) throw new Error('Nessuna organizzazione attiva');
      const { data, error } = await supabase
        .from('suppliers')
        .insert({
          organization_id: currentOrganizationId,
          name: input.name.trim(),
          slug: (input.slug || slugify(input.name)).slice(0, 60),
          website: input.website?.trim() || null,
          contact_email: input.contact_email?.trim() || null,
          contact_phone: input.contact_phone?.trim() || null,
          default_discount: input.default_discount?.trim() || null,
          notes: input.notes?.trim() || null,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers-list'] });
      toast.success('Fornitore creato');
    },
    onError: (e: any) => toast.error(`Errore: ${e.message}`),
  });
};

export const useUpdateSupplier = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: { id: string } & Partial<CreateSupplierInput> & { is_active?: boolean }) => {
      const { error } = await supabase.from('suppliers').update(patch).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers-list'] });
      toast.success('Fornitore aggiornato');
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) => toast.error(`Errore: ${e.message}`),
  });
};

/**
 * Elimina un fornitore proprio dell'organizzazione (i fornitori globali sono
 * filtrati a livello UI). RLS consente delete agli owner/admin/supervisor/technician.
 * I materiali collegati conservano il loro supplier_id come stringa storico (FK
 * non esiste, e' una text reference) — quindi il delete non rompe il catalogo
 * ma il fornitore sparisce dalla lista filtri/picker.
 */
export const useDeleteSupplier = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('suppliers').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['suppliers-list'] });
      toast.success('Fornitore eliminato');
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) => toast.error(`Errore: ${e.message}`),
  });
};

// =====================================================================
// Family CRUD (supplier_product_families)
// =====================================================================
interface CreateFamilyInput {
  supplier_id: string;
  code: string;
  name: string;
  category?: string | null;
  brand?: string | null;
  description?: string | null;
  sort_order?: number;
}

export const useCreateFamily = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateFamilyInput) => {
      const { data, error } = await supabase
        .from('supplier_product_families')
        .insert({
          supplier_id: input.supplier_id,
          code: input.code.trim(),
          name: input.name.trim(),
          category: input.category?.trim() || null,
          brand: input.brand?.trim() || null,
          description: input.description?.trim() || null,
          sort_order: input.sort_order ?? 0,
          is_active: true,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['supplier-families', vars.supplier_id] });
      toast.success('Famiglia creata');
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) => toast.error(`Errore: ${e.message}`),
  });
};

interface UpdateFamilyInput {
  id: string;
  supplier_id: string;          // per invalidare la query corretta
  code: string;
  name: string;
  category?: string | null;
  brand?: string | null;
  description?: string | null;
  /** Se la famiglia ha materiali assegnati, il backend rifiuta il cambio
   *  categoria (potrebbe rompere il classifier nel configuratore). Il client
   *  deve già impedire l'invio in quel caso, ma manteniamo un secondo livello. */
}

export const useUpdateFamily = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateFamilyInput) => {
      const { error } = await supabase
        .from('supplier_product_families')
        .update({
          code: input.code.trim(),
          name: input.name.trim(),
          category: input.category?.trim() || null,
          brand: input.brand?.trim() || null,
          description: input.description?.trim() || null,
        })
        .eq('id', input.id);
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['supplier-families', vars.supplier_id] });
      toast.success('Famiglia aggiornata');
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) => toast.error(`Errore: ${e.message}`),
  });
};

export const useDeleteFamily = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, supplier_id: _ }: { id: string; supplier_id: string }) => {
      // I materiali collegati conservano family_id come stringa storico (FK ON DELETE SET NULL).
      const { error } = await supabase.from('supplier_product_families').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: (_data, vars) => {
      queryClient.invalidateQueries({ queryKey: ['supplier-families', vars.supplier_id] });
      toast.success('Famiglia eliminata');
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onError: (e: any) => toast.error(`Errore: ${e.message}`),
  });
};

// =====================================================================
// Customer discount upsert / delete (set/clear discount for a family)
// =====================================================================
interface UpsertDiscountInput {
  supplier_id: string;
  family_id: string;
  discounts: number[];
  valid_until?: string | null;
  notes?: string | null;
}

export const useUpsertDiscount = () => {
  const { currentOrganizationId } = useCurrentOrganization();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpsertDiscountInput) => {
      if (!currentOrganizationId) throw new Error('Nessuna organizzazione attiva');
      const cleaned = input.discounts.filter(d => Number.isFinite(d) && d > 0 && d < 100);
      const { error } = await supabase
        .from('customer_discounts')
        .upsert(
          {
            organization_id: currentOrganizationId,
            supplier_id: input.supplier_id,
            family_id: input.family_id,
            discounts: cleaned,
            valid_until: input.valid_until ?? null,
            notes: input.notes ?? null,
          },
          { onConflict: 'organization_id,supplier_id,family_id' },
        );
      if (error) throw error;
    },
    onSuccess: (_, vars) => {
      queryClient.invalidateQueries({ queryKey: ['supplier-families', vars.supplier_id] });
      queryClient.invalidateQueries({ queryKey: ['suppliers-list'] });
      queryClient.invalidateQueries({ queryKey: ['materials'] });
      toast.success('Sconto aggiornato');
    },
    onError: (e: any) => toast.error(`Errore: ${e.message}`),
  });
};

export const useDeleteDiscount = () => {
  const { currentOrganizationId } = useCurrentOrganization();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (discountId: string) => {
      const { error } = await supabase.from('customer_discounts').delete().eq('id', discountId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['supplier-families'] });
      queryClient.invalidateQueries({ queryKey: ['suppliers-list'] });
      queryClient.invalidateQueries({ queryKey: ['materials'] });
      toast.success('Sconto rimosso');
    },
    onError: (e: any) => toast.error(`Errore: ${e.message}`),
  });
};
